import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import globalSettingsBranches from '../../../../../stores/global-settings-branches';
import { getHebrewYear } from '../../../../../utils/hebrew-date-utils';
import { FilterExpanderWithListOfCheckboxes } from '../../../../../components/CustomExpander/componenets/filter-expander-with-list-of-checkboxes';
import { CustomExpander } from '../../../../../components/CustomExpander/CustomExpander';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { CoursesExceedingHoursFilterDatamodel } from './courses-exceeding-hours-filter-datamodel';
import { CourseStatus } from '../../../../../api/types/programs/course/course-status';

interface YearOption {
  year: string;
  code: string;
  value: number;
}

interface CoursesExceedingHoursFilterProps {
  onSearch: (filters: CoursesExceedingHoursFilterDatamodel) => void;
}

export const CoursesExceedingHoursFilter = observer(
  ({ onSearch }: CoursesExceedingHoursFilterProps) => {
    const [selectedBranches, setSelectedBranches] = useState<number[]>([]);
    const [selectedYears, setSelectedYears] = useState<number[]>([]);
    const [selectedStatuses, setSelectedStatuses] = useState<CourseStatus[]>(
      [],
    );
    const [exceedingHours, setExceedingHours] = useState<number | null>(null);

    // Generate years array
    const years: YearOption[] = [];
    for (let year = 1980; year <= 2030; year++) {
      years.push({
        year: `${getHebrewYear(year)} (${year} - ${year + 1})`,
        code: 'year',
        value: year,
      });
    }

    const courseStatuses = [
      { id: 'active', name: 'פעיל' },
      { id: 'completed', name: 'הסתיים' },
      { id: 'cancelled', name: 'בוטל' },
    ];

    const handleSearch = () => {
      const filters: CoursesExceedingHoursFilterDatamodel = {
        branchIds: selectedBranches,
        startYears: selectedYears,
        courseStatuses: selectedStatuses,
        exceedingHours,
      };
      onSearch(filters);
    };

    const styles = {
      container: {
        height: '100%',
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column' as const,
      },
      header: {
        fontSize: '1.2rem',
        fontWeight: 'bold',
        marginBottom: '16px',
        color: '#333',
      },
      content: {
        flex: 1,
        overflowY: 'auto' as const,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        paddingRight: '4px',
      },
      exceedingHoursContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        padding: '8px',
      },
      label: {
        fontSize: '0.9rem',
        color: '#666',
      },
      buttonContainer: {
        marginTop: '16px',
        padding: '8px',
        borderTop: '1px solid #e9ecef',
      },
      searchButton: {
        width: '100%',
        backgroundColor: '#2196F3',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        padding: '12px',
        fontSize: '1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
      },
    };

    return (
      <div style={styles.container}>
        <div style={styles.header}>סינונים</div>
        <div style={styles.content}>
          <FilterExpanderWithListOfCheckboxes
            title="שלוחות"
            items={globalSettingsBranches.branches}
            selectedItems={selectedBranches}
            onItemToggle={(branchId) => {
              setSelectedBranches((prev) =>
                prev.includes(branchId)
                  ? prev.filter((b) => b !== branchId)
                  : [...prev, branchId],
              );
            }}
            getItemLabel={(branch) => branch.branchName}
            getItemKey={(branch) => branch.id}
            defaultExpanded={true}
            searchPlaceholder="חפש שלוחה..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="שנת התחלה"
            items={years}
            selectedItems={selectedYears.map(String)}
            onItemToggle={(yearStr) => {
              const year = parseInt(yearStr);
              setSelectedYears((prev) =>
                prev.includes(year)
                  ? prev.filter((y) => y !== year)
                  : [...prev, year],
              );
            }}
            getItemLabel={(yearOption) => yearOption.year}
            getItemKey={(yearOption) => yearOption.value.toString()}
            searchPlaceholder="חפש שנה..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="סטטוס קורס"
            items={Object.values(CourseStatus).filter(
              (status) => typeof status === 'string',
            )}
            selectedItems={selectedStatuses}
            onItemToggle={(status) => {
              setSelectedStatuses((prev) =>
                prev.includes(status as CourseStatus)
                  ? prev.filter((s) => s !== status)
                  : [...prev, status as CourseStatus],
              );
            }}
            getItemLabel={(status) =>
              CourseStatus.getDisplayText(status as CourseStatus)
            }
            getItemKey={(status) => status}
            searchPlaceholder="חפש סטטוס..."
          />

          <CustomExpander>
            <div>חריגה</div>
            <div style={styles.exceedingHoursContainer}>
              <span style={styles.label}>חריגה החל מ:</span>
              <InputNumber
                value={exceedingHours}
                onValueChange={(e) => setExceedingHours(e.value ?? null)}
                mode="decimal"
                minFractionDigits={1}
                maxFractionDigits={2}
                min={0}
                placeholder="הזן מספר שעות"
                style={{ width: '100%' }}
              />
            </div>
          </CustomExpander>
        </div>
        <div style={styles.buttonContainer}>
          <Button
            label="חפש"
            icon="pi pi-search"
            style={styles.searchButton}
            onClick={handleSearch}
          />
        </div>
      </div>
    );
  },
);
